import React from 'react';
import { useLanguage } from '../context/LanguageContext';

/**
 * Call Now Section Component - Modern Redesign
 * Features:
 * - Modern glassmorphism design
 * - Animated phone icon with ring effect
 * - Gradient background with animated particles
 * - Responsive design
 */
const CallNow = () => {
  const { t } = useLanguage();

  return (
    <section className="relative overflow-hidden bg-gradient-to-br from-primary-bronze via-[#7a5230] to-[#5a3a1a] py-12 sm:py-16 px-4 sm:px-6 lg:px-8">
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-0 left-1/4 w-72 h-72 bg-white/5 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-white/5 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-white/5 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '2s' }} />
      </div>

      <div className="max-w-7xl mx-auto relative z-10">
        <div className="flex flex-col lg:flex-row items-center justify-between gap-8 lg:gap-12">
          {/* Left Side - Icon & Text */}
          <div className="flex-1 flex flex-col sm:flex-row items-center lg:items-start gap-6 lg:gap-8 text-center lg:text-left">
            {/* Animated Phone Icon */}
            <div className="relative">
              <div className="relative w-20 h-20 sm:w-24 sm:h-24 flex items-center justify-center">
                {/* Ring Animation */}
                <div className="absolute inset-0 rounded-full border-4 border-white/30 animate-ping" />
                <div className="absolute inset-2 rounded-full border-4 border-white/20 animate-ping" style={{ animationDelay: '0.5s' }} />
                
                {/* Phone Icon */}
                <div className="relative z-10 bg-white/20 backdrop-blur-md rounded-2xl p-4 sm:p-5 shadow-2xl transform hover:scale-110 transition-transform duration-300">
                  <svg
                    className="w-10 h-10 sm:w-12 sm:h-12 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                    />
                  </svg>
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="flex-1">
              <h3 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-3 sm:mb-4 leading-tight">
                {t('home.callNowTitle')}
              </h3>
              <p className="text-white/90 text-base sm:text-lg leading-relaxed max-w-md mx-auto lg:mx-0">
                {t('home.callNowText')}
              </p>
            </div>
          </div>

          {/* Right Side - Modern Phone Button */}
          <div className="flex-shrink-0">
            <a
              href="tel:+393278872023"
              className="group relative inline-flex items-center justify-center gap-3 text-lg sm:text-xl font-bold text-white px-8 sm:px-10 py-4 sm:py-5 bg-white/10 backdrop-blur-md border-2 border-white/30 rounded-2xl hover:bg-white/20 hover:border-white/50 transition-all duration-300 shadow-2xl hover:shadow-3xl hover:-translate-y-2 whitespace-nowrap overflow-hidden"
            >
              {/* Shimmer Effect */}
              <span className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent transform -skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />
              
              {/* Phone Icon */}
              <svg
                className="w-5 h-5 sm:w-6 sm:h-6 transform group-hover:rotate-12 transition-transform duration-300"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                />
              </svg>
              
              {/* Phone Number */}
              <span className="relative z-10">+39 327 887 2023</span>
              
              {/* Glow Effect */}
              <span className="absolute inset-0 rounded-2xl bg-white/20 opacity-0 group-hover:opacity-100 blur-xl transition-opacity duration-300" />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CallNow;

